import React, { useEffect, useState } from 'react';
import { Table, DOMHelper, Divider, SortType, Tag, Stack, Input, Checkbox, Button, IconButton, Whisper, Tooltip, toaster, Message } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import { RunningClubAlbum, ClubAlbumPaginationFilterRequest, ClubAlbumStatusList, ClubAlbumKind, ClubAlbumProviderList } from '@/models/running-club-album.model';
import { deleteRunningClubAlbum, getPagedRunningClubAlbums } from '@/services/running-club-album.service';
import RunningClubExternalAlbumDrawer from './running-club-external-album-drawer';
import { Icon } from '@rsuite/icons';
import { MdDelete, MdEdit, MdOpenInNew } from 'react-icons/md';
import PageContent from '@/components/PageContent';
import { DateFormatOptions } from '@/models/core.model';
import FetchingSelect from '@/components/FetchingSelect';
import { getRunningClubEventsLookup } from '@/services/running-club-event.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

export interface RunningClubAlbumListProps {
  runningClubId?: number;
  onSelectAlbum?: (album: RunningClubAlbum) => void;
}

const RunningClubAlbumList: React.FC<RunningClubAlbumListProps> = ({ runningClubId, onSelectAlbum }) => {
  const [filterReq, setFilterReq] = useState<ClubAlbumPaginationFilterRequest>(new ClubAlbumPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<RunningClubAlbum[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<RunningClubAlbum | undefined>(undefined);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: ClubAlbumPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        runningClubId: runningClubId ?? req.runningClubId,
      };
      const result: PagedResponseModel<RunningClubAlbum> = await getPagedRunningClubAlbums(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Running Club Albums:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const isExternal = (album: RunningClubAlbum) => album.kind === ClubAlbumKind.External;

  const openCreate = () => {
    setEditingAlbum(undefined);
    setOpenDrawer(true);
  };

  const openEdit = (album: RunningClubAlbum) => {
    setEditingAlbum(album);
    setOpenDrawer(true);
  };

  // Soft delete for either kind; only external albums can be edited from here.
  const onDeleteItem = (album: RunningClubAlbum) => {
    const handleYesClick = async () => {
      setLoading(true);
      try {
        const result: BaseResponseModel<null> = await deleteRunningClubAlbum(album.id);
        if (result.success) {
          toastSuccessMessage(`Album removed.`);
          fetchData(filterReq);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in deleting album:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
        toaster.clear();
      }
    };

    toaster.push(
      <Message type="error" closable>
        <p style={{ marginBottom: 10 }}>
          Remove <strong>{album.name}</strong>?{' '}
          {isExternal(album)
            ? 'The photos stay where they are hosted.'
            : 'Its photos will no longer be visible in the club.'}
        </p>
        <div>
          <Button appearance="primary" color='red' onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={() => toaster.clear()} style={{ marginLeft: 10 }}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={openCreate}>
          New External Album
        </Button>
        <Stack spacing={6}>
          <FetchingSelect
            label="Event"
            fetchData={(text, page) => getRunningClubEventsLookup(text, runningClubId, page)}
            value={filterReq.runningClubEventId}
            onChange={dataKey => setFilterReq(pre => ({...pre, runningClubEventId: dataKey}))}
          />               
          <Input
            placeholder="Keyword"
            value={filterReq.keyword ?? ''}
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
          <Checkbox onChange={(value, checked) => setFilterReq(pre => ({...pre, eventAlbumsOnly: checked == true ? true : undefined}))} >Only Event Albums</Checkbox>
        </Stack>
      </Stack>
            
      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
        rowHeight={60}
        onRowClick={rowData => {
          const album = rowData as RunningClubAlbum;
          // External albums have no images here; editing happens via the row actions.
          if (!isExternal(album) && onSelectAlbum) onSelectAlbum(album);
        }}
      >
        <Column flexGrow={1} align="left" verticalAlign="middle" sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle">
          <HeaderCell>Kind</HeaderCell>
          <Cell>
            {(rowData: RunningClubAlbum) => isExternal(rowData)
              ? (
                <a href={rowData.externalUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                  <Tag color="blue">
                    {ClubAlbumProviderList.find(p => p.value === rowData.externalProvider)?.label ?? 'External'}
                    {' '}<Icon as={MdOpenInNew} />
                  </Tag>
                </a>
              )
              : <Tag>In Elites</Tag>}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle">
          <HeaderCell>Event</HeaderCell>
          <Cell>
            {(rowData: RunningClubAlbum) => rowData.runningClubEvent?.name ?? '—'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" verticalAlign="middle">
          <HeaderCell>Status</HeaderCell>
          <Cell>
            {(rowData: RunningClubAlbum) => (
              <Tag color={rowData.status === 1 ? 'green' : 'orange'}>
                {ClubAlbumStatusList.find(s => s.value === rowData.status)?.label ?? rowData.status}
              </Tag>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" verticalAlign="middle" sortable>
          <HeaderCell>Images</HeaderCell>
          <Cell dataKey="imageCount" />
        </Column>
        <Column flexGrow={1} align="center" verticalAlign="middle">
          <HeaderCell>Pending</HeaderCell>
          <Cell dataKey="pendingImageCount" />
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle" sortable>
          <HeaderCell>Created</HeaderCell>
          <Cell>
            {(rowData: RunningClubAlbum) => rowData.createdAt ? new Date(rowData.createdAt).toLocaleDateString(undefined, DateFormatOptions) : ''}
          </Cell>
        </Column>
        <Column width={110} align="left" verticalAlign="middle" fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: RunningClubAlbum) => (
              <Stack spacing={8} onClick={e => e.stopPropagation()}>
                {isExternal(rowData) && (
                  <Whisper placement="top" trigger="hover" speaker={<Tooltip>Edit</Tooltip>}>
                    <IconButton circle size="sm" appearance="ghost"
                      onClick={() => openEdit(rowData)}
                      icon={<Icon as={MdEdit} />} />
                  </Whisper>
                )}
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Delete</Tooltip>}>
                  <IconButton circle size="sm" color="red" appearance="ghost"
                    onClick={() => onDeleteItem(rowData)}
                    icon={<Icon as={MdDelete} />} />
                </Whisper>
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({ ...pre, pageNumber: dataKey }))}
        onChangeLimit={dataKey => setFilterReq(pre => ({ ...pre, pageSize: dataKey }))}
      />
      <RunningClubExternalAlbumDrawer
        runningClubId={runningClubId}
        album={editingAlbum}
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onSaved={() => fetchData(filterReq)}
      />
    </PageContent>
  );
};

export default RunningClubAlbumList;
